import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import emailService from '../services/emailService';
import authService from '../services/authService';
import EmailAttachments from '../components/EmailAttachments';
import { decodeEmailHeader, formatLongDate, getSentimentBadge, isHtmlContent } from '../utils/emailUtils';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './EmailDetail.css';

function EmailDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const emailContentRef = useRef(null);

  // Récupérer les paramètres de navigation
  const fromSearch = searchParams.get('from') === 'search';
  const searchQuery = searchParams.get('search') || '';

  // Générer l'URL de retour appropriée
  const getBackUrl = () => {
    if (fromSearch && searchQuery) {
      return `/emails?search=${encodeURIComponent(searchQuery)}`;
    }
    return '/emails';
  };

  useEffect(() => {
    loadEmail();
  }, [id]);

  const loadEmail = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await emailService.getEmail(id);
      setEmail(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleExportPDF = async () => {
    if (!emailContentRef.current) {
      alert("Le contenu de l'email n'est pas disponible pour l'export.");
      return;
    }
    setExportingPDF(true);

    try {
      // Laisser le temps aux styles d'export (désactivation du clamp, etc.)
      await new Promise(resolve => setTimeout(resolve, 120));

      // Capture haute résolution
      const canvas = await html2canvas(emailContentRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        height: emailContentRef.current.scrollHeight,
        width: emailContentRef.current.scrollWidth,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });

      // Dimensions PDF (mm) et image dessinée (mm)
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10; // mm
      const drawWidth = pdfWidth - margin * 2;

      // Echelle entre pixels canvas et mm PDF
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const drawHeight = (drawWidth / canvasWidth) * canvasHeight; // hauteur de l'image si on imprimait entière
      const pageContentHeightMM = pdfHeight - margin * 2;

      // Hauteur d'une page en pixels canvas (pour découper sans chevauchement)
      const pageHeightPx = (pageContentHeightMM / drawHeight) * canvasHeight;

      const ctx = canvas.getContext('2d');
      let remainingHeightPx = canvasHeight;
      let offsetY = 0;
      let pageIndex = 0;

      while (remainingHeightPx > 0) {
        const sliceHeightPx = Math.min(pageHeightPx, remainingHeightPx);

        // Créer un canvas temporaire pour la tranche de page
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvasWidth;
        pageCanvas.height = Math.floor(sliceHeightPx);
        const pageCtx = pageCanvas.getContext('2d');

        // Dessiner la tranche depuis le canvas source
        pageCtx.drawImage(
          canvas,
          0, Math.floor(offsetY),                // src x, y
          canvasWidth, Math.floor(sliceHeightPx), // src w, h
          0, 0,                                   // dst x, y
          canvasWidth, Math.floor(sliceHeightPx)  // dst w, h
        );

        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageDrawHeightMM = (drawWidth / canvasWidth) * sliceHeightPx;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(pageImgData, 'PNG', margin, margin, drawWidth, pageDrawHeightMM, undefined, 'FAST');

        // Avancer
        offsetY += sliceHeightPx;
        remainingHeightPx -= sliceHeightPx;
        pageIndex += 1;
      }

      pdf.save(`email_${email.id}.pdf`);
    } catch (err) {
      console.error("Erreur lors de la génération du PDF:", err);
      alert(`Erreur lors de l'export PDF: ${err.message}`);
    } finally {
      setExportingPDF(false);
    }
  };


  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2">Chargement de l'email...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
        <Link to={getBackUrl()} className="btn btn-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Retour aux emails
        </Link>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning">Email non trouvé</div>
        <Link to={getBackUrl()} className="btn btn-primary">
          <i className="bi bi-arrow-left me-2"></i>
          Retour aux emails
        </Link>
      </div>
    );
  }

  const sentiment = email.analysis?.sentiment;
  const sentimentBadge = getSentimentBadge(sentiment);

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="page-header d-flex justify-content-between align-items-start">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={getBackUrl()}>Emails</Link>
                  </li>
                  <li className="breadcrumb-item active">Détail</li>
                </ol>
              </nav>
              <h1 className="page-title">
                {decodeEmailHeader(email.subject) || '(Sans sujet)'}
              </h1>
            </div>
            <div>
              <button
                className="btn btn-outline-danger me-2"
                onClick={handleExportPDF}
                disabled={exportingPDF}
                title="Exporter cet email en PDF"
              >
                {exportingPDF ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Export...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                    Export PDF
                  </>
                )}
              </button>

              <Link to={getBackUrl()} className="btn btn-outline-primary">
                <i className="bi bi-arrow-left me-2"></i>
                Retour
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Email Content */}
        <div className="col-lg-8">
          <div className={`card mb-4 ${exportingPDF ? 'exporting-pdf' : ''}`} ref={emailContentRef}>
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-envelope me-2"></i>
                Contenu de l'email
              </h5>
            </div>
            <div className="card-body">
              {/* Email Metadata */}
              <div className="row mb-3">
                <div className="col-sm-3"><strong>De:</strong></div>
                <div className="col-sm-9">
                  {email.sender_name && (
                    <span>{email.sender_name} </span>
                  )}
                  <span className="text-muted">{'<'}{email.sender}{'>'}</span>
                </div>
              </div>

              {email.recipient && (
                <div className="row mb-3">
                  <div className="col-sm-3"><strong>À:</strong></div>
                  <div className="col-sm-9">{email.recipient}</div>
                </div>
              )}
              {(email.cc || '').trim() && (
                <div className="row mb-3">
                  <div className="col-sm-3"><strong>Cc:</strong></div>
                  <div className="col-sm-9">
                    <div
                      className="cc-inline"
                      title={(email.cc || '').trim() ? email.cc : ''}
                    >
                      {(email.cc || '').trim()}
                    </div>
                  </div>
                </div>
              )}

              <div className="row mb-3">
                <div className="col-sm-3"><strong>Date:</strong></div>
                <div className="col-sm-9">
                  {formatLongDate(email.date)}
                </div>
              </div>

              {/* Email Body */}
              <hr />
              <div className="email-body">
                <h6>Message:</h6>
                {(() => {
                  const content = email.content || email.body || email.text_content || 'Contenu non disponible';

                  if (isHtmlContent(content)) {
                    return (
                      <div
                        className="border rounded p-3 bg-light"
                        style={{
                          maxHeight: exportingPDF ? 'none' : '400px',
                          overflowY: exportingPDF ? 'visible' : 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: content }}
                      />
                    );
                  } else {
                    return (
                      <div
                        className="border rounded p-3 bg-light"
                        style={{
                          whiteSpace: 'pre-wrap',
                          maxHeight: exportingPDF ? 'none' : '400px',
                          overflowY: exportingPDF ? 'visible' : 'auto'
                        }}
                      >
                        {content}
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Pièces jointes */}
              <EmailAttachments
                emailId={id}
                token={authService.getToken()}
              />
            </div>
          </div>
        </div>

        {/* Analysis Sidebar */}
        <div className="col-lg-4">
          {email.analysis ? (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="bi bi-graph-up me-2"></i>
                  Analyse IA
                </h5>
              </div>
              <div className="card-body">
                {/* Sentiment */}
                {sentiment && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-heart me-2"></i>
                      Sentiment
                    </h6>
                    <span className={`badge ${sentimentBadge.class} fs-6`}>
                      <i className={`bi ${sentimentBadge.icon} me-2`}></i>
                      {sentimentBadge.text}
                    </span>
                    {email.analysis.result && email.analysis.result.sentiment && email.analysis.result.sentiment.score && (
                      <div className="mt-2">
                        <small className="text-muted">
                          Score: {(email.analysis.result.sentiment.score * 100).toFixed(1)}%
                        </small>
                      </div>
                    )}
                  </div>
                )}

                {/* Category */}
                {email.analysis.category && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-tag me-2"></i>
                      Catégorie
                    </h6>
                    <span className="badge bg-info">{email.analysis.category}</span>
                  </div>
                )}

                {/* Priority */}
                {email.analysis.priority && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-exclamation-circle me-2"></i>
                      Priorité
                    </h6>
                    <span className={`badge ${email.analysis.priority === 'high' ? 'bg-danger' :
                        email.analysis.priority === 'medium' ? 'bg-warning' : 'bg-success'
                      }`}>
                      {email.analysis.priority === 'high' ? 'Élevée' :
                        email.analysis.priority === 'medium' ? 'Moyenne' : 'Faible'}
                    </span>
                  </div>
                )}

                {/* Entities */}
                {email.analysis.entities && email.analysis.entities.length > 0 && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-people me-2"></i>
                      Entités détectées
                    </h6>
                    <div className="d-flex flex-wrap gap-1">
                      {email.analysis.entities.map((entity, index) => (
                        <span key={index} className="badge bg-secondary">
                          {entity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {email.analysis.summary && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-file-text me-2"></i>
                      Résumé
                    </h6>
                    <p className="small">{email.analysis.summary}</p>
                  </div>
                )}

                {/* Action Items */}
                {email.analysis.action_items && email.analysis.action_items.length > 0 && (
                  <div className="analysis-section">
                    <h6>
                      <i className="bi bi-check-square me-2"></i>
                      Actions suggérées
                    </h6>
                    <ul className="list-unstyled">
                      {email.analysis.action_items.map((action, index) => (
                        <li key={index} className="small mb-1">
                          <i className="bi bi-arrow-right me-2"></i>
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-muted small mt-3">
                  <i className="bi bi-clock me-1"></i>
                  Analysé le {formatLongDate(email.analysis.created_at || email.created_at)}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-center">
                <i className="bi bi-chat-dots display-3 text-muted mb-3"></i>
                <h5>Analyse via Chatbot</h5>
                <p className="text-muted">
                  Utilisez le chatbot pour analyser vos emails et obtenir des insights personnalisés.
                </p>
                <Link to="/chatbot" className="btn btn-primary">
                  <i className="bi bi-chat-dots me-2"></i>
                  Aller au Chatbot
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailDetail;